import type { WidgetSize } from './DragDropLayout';

type ColumnCard = { id: string; rowSpan: number };

const MINI_UNITS: Record<WidgetSize, number> = {
  mini: 3,
  regular: 6,
  large: 9,
};

const getRowSpan = (size: WidgetSize) => MINI_UNITS[size];

export function fitNativeSectionColumnsToCount<T>(columns: T[][], columnCount: number): T[][] {
  const normalizedColumnCount = Math.max(1, Math.floor(columnCount || 0));
  return Array.from({ length: normalizedColumnCount }, (_, index) => [...(columns[index] ?? [])]);
}

const buildPreferredColumnOrder = (columnCount: number) => {
  const normalizedColumnCount = Math.max(1, Math.floor(columnCount || 0));
  const order: number[] = [];

  if (normalizedColumnCount % 2 === 1) {
    const center = Math.floor(normalizedColumnCount / 2);
    order.push(center);
    for (let offset = 1; order.length < normalizedColumnCount; offset += 1) {
      const left = center - offset;
      const right = center + offset;
      if (left >= 0) order.push(left);
      if (right < normalizedColumnCount) order.push(right);
    }
    return order;
  }

  const leftCenter = normalizedColumnCount / 2 - 1;
  const rightCenter = leftCenter + 1;
  order.push(leftCenter, rightCenter);

  for (let offset = 1; order.length < normalizedColumnCount; offset += 1) {
    const left = leftCenter - offset;
    const right = rightCenter + offset;
    if (left >= 0) order.push(left);
    if (right < normalizedColumnCount) order.push(right);
  }

  return order;
};

export function buildSparseNativeSectionColumns(
  visibleIds: string[],
  columnCount: number,
  getWidgetSize: (widgetId: string) => WidgetSize,
): ColumnCard[][] {
  const normalizedColumnCount = Math.max(1, Math.floor(columnCount || 0));
  const columns = Array.from({ length: normalizedColumnCount }, () => ({ items: [] as ColumnCard[] }));
  const columnHeights = Array.from({ length: normalizedColumnCount }, () => 0);
  const preferredOrder = buildPreferredColumnOrder(normalizedColumnCount);

  const pushToColumn = (columnIndex: number, widgetId: string) => {
    const targetColumn = columns[columnIndex];
    if (!targetColumn) return;
    const rowSpan = getRowSpan(getWidgetSize(widgetId));
    targetColumn.items.push({ id: widgetId, rowSpan });
    columnHeights[columnIndex] += rowSpan;
  };

  if (visibleIds.length === 2) {
    if (normalizedColumnCount === 2) {
      pushToColumn(0, visibleIds[0]);
      pushToColumn(1, visibleIds[1]);
      return columns.map((column) => column.items);
    }

    if (normalizedColumnCount === 3) {
      pushToColumn(1, visibleIds[0]);
      pushToColumn(1, visibleIds[1]);
      return columns.map((column) => column.items);
    }

    if (normalizedColumnCount === 4) {
      pushToColumn(1, visibleIds[0]);
      pushToColumn(2, visibleIds[1]);
      return columns.map((column) => column.items);
    }
  }

  visibleIds.forEach((widgetId) => {
    let targetIndex = 0;
    let shortestHeight = Number.POSITIVE_INFINITY;

    for (let columnIndex = 0; columnIndex < normalizedColumnCount; columnIndex += 1) {
      const height = columnHeights[columnIndex] ?? 0;
      if (height < shortestHeight) {
        shortestHeight = height;
        targetIndex = columnIndex;
        continue;
      }

      if (height === shortestHeight) {
        const currentPreferredRank = preferredOrder.indexOf(columnIndex);
        const bestPreferredRank = preferredOrder.indexOf(targetIndex);
        if (currentPreferredRank !== -1 && (bestPreferredRank === -1 || currentPreferredRank < bestPreferredRank)) {
          targetIndex = columnIndex;
        }
      }
    }

    pushToColumn(targetIndex, widgetId);
  });

  return columns.map((column) => column.items);
}
